// Dialogs for the superadmin model catalogue: add/edit a chat model, add/edit an
// embedding model, and discard one. Kept apart from the page so the page reads
// as the table it is.
//
// The discard dialog deliberately does not say *which* tenants use the model:
// that is tenant data, and this console never reads a tenant's database. It
// states the consequence instead; each tenant sees the impact on their own
// screens.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Input, Label, Textarea, selectClass } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import {
  createPlatformEmbeddingModel,
  createPlatformLlmModel,
  discardPlatformModel,
  updatePlatformEmbeddingModel,
  updatePlatformLlmModel,
} from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type {
  LlmProvider,
  PlatformEmbeddingModel,
  PlatformLlmModel,
  PlatformModelKind,
} from "@/types/api";

const PROVIDERS: LlmProvider[] = ["openai", "anthropic", "gemini"];

/** A non-negative decimal as typed — kept as a string all the way to the API. */
const isPrice = (s: string) => s.trim() !== "" && Number.isFinite(Number(s)) && Number(s) >= 0;
const isPositiveInt = (s: string) => /^\d+$/.test(s.trim()) && Number(s) > 0;

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {children}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        className="h-4 w-4 accent-[hsl(var(--primary))]"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

// ---- Chat (LLM) model --------------------------------------------------------

export function LlmModelDialog({
  open,
  onOpenChange,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` adds a new model; a row edits that one. */
  row: PlatformLlmModel | null;
}) {
  const qc = useQueryClient();
  const [modelId, setModelId] = React.useState("");
  const [provider, setProvider] = React.useState<LlmProvider>("openai");
  const [input, setInput] = React.useState("");
  const [output, setOutput] = React.useState("");
  const [context, setContext] = React.useState("");
  const [tools, setTools] = React.useState(true);
  const [streaming, setStreaming] = React.useState(true);
  const [jsonMode, setJsonMode] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setModelId(row?.model_id ?? "");
    setProvider((row?.provider as LlmProvider | undefined) ?? "openai");
    setInput(row ? String(row.input_per_1m) : "");
    setOutput(row ? String(row.output_per_1m) : "");
    setContext(row ? String(row.context_window) : "");
    setTools(row?.supports_tools ?? true);
    setStreaming(row?.supports_streaming ?? true);
    setJsonMode(row?.supports_json_mode ?? false);
  }, [open, row]);

  const save = useMutation({
    mutationFn: () => {
      const fields = {
        input_per_1m: input.trim(),
        output_per_1m: output.trim(),
        context_window: Number(context),
        supports_tools: tools,
        supports_streaming: streaming,
        supports_json_mode: jsonMode,
      };
      return row
        ? updatePlatformLlmModel(row.model_id, fields)
        : createPlatformLlmModel({ model_id: modelId.trim(), provider, ...fields });
    },
    onSuccess: () => {
      toast.success(row ? "Model updated." : "Model added — tenants can select it now.");
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: qk.platformModels("llm") });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid =
    (row !== null || modelId.trim() !== "") &&
    isPrice(input) &&
    isPrice(output) &&
    isPositiveInt(context);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{row ? `Edit ${row.model_id}` : "Add an LLM model"}</DialogTitle>
          <DialogDescription>
            {row
              ? "Price changes apply to the cost of every run from now on."
              : "Tenants will be able to select it for their agents straight away."}
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          {row ? null : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="pm-id"
                label="Model id"
                hint="Exactly what the provider expects on the wire."
              >
                <Input
                  id="pm-id"
                  value={modelId}
                  onChange={(e) => setModelId(e.target.value)}
                  placeholder="gpt-5.6-terra"
                  autoFocus
                />
              </Field>
              <Field id="pm-provider" label="Provider">
                <select
                  id="pm-provider"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as LlmProvider)}
                  className={selectClass}
                >
                  {PROVIDERS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="pm-input" label="Input price (USD / 1M tokens)">
              <Input
                id="pm-input"
                inputMode="decimal"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="2.00"
              />
            </Field>
            <Field id="pm-output" label="Output price (USD / 1M tokens)">
              <Input
                id="pm-output"
                inputMode="decimal"
                value={output}
                onChange={(e) => setOutput(e.target.value)}
                placeholder="12.00"
              />
            </Field>
          </div>

          <Field id="pm-context" label="Context window (tokens)">
            <Input
              id="pm-context"
              inputMode="numeric"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="1050000"
            />
          </Field>

          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <Check label="Tool calls" checked={tools} onChange={setTools} />
            <Check label="Streaming" checked={streaming} onChange={setStreaming} />
            <Check label="JSON mode" checked={jsonMode} onChange={setJsonMode} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? "Saving…" : row ? "Save" : "Add model"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Embedding model ---------------------------------------------------------

export function EmbeddingModelDialog({
  open,
  onOpenChange,
  row,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: PlatformEmbeddingModel | null;
}) {
  const qc = useQueryClient();
  const [modelId, setModelId] = React.useState("");
  const [dimensions, setDimensions] = React.useState("");
  const [price, setPrice] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setModelId(row?.model_id ?? "");
    setDimensions(row?.dimensions ? String(row.dimensions) : "");
    setPrice(row ? String(row.price_per_1m) : "");
  }, [open, row]);

  const save = useMutation({
    mutationFn: () => {
      const dims = dimensions.trim() ? Number(dimensions) : null;
      return row
        ? updatePlatformEmbeddingModel(row.model_id, {
            price_per_1m: price.trim(),
            ...(dims ? { dimensions: dims } : {}),
          })
        : createPlatformEmbeddingModel({
            model_id: modelId.trim(),
            dimensions: dims,
            price_per_1m: price.trim(),
          });
    },
    onSuccess: () => {
      toast.success(row ? "Model updated." : "Model added — tenants can select it now.");
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: qk.platformModels("embedding") });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const valid =
    (row !== null || modelId.trim() !== "") &&
    isPrice(price) &&
    (dimensions.trim() === "" || isPositiveInt(dimensions));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{row ? `Edit ${row.model_id}` : "Add an embedding model"}</DialogTitle>
          <DialogDescription>
            Embeddings run on OpenAI today. A knowledge base&apos;s vectors belong to the model that
            produced them, so tenants pick this once per knowledge base.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) save.mutate();
          }}
        >
          {row ? null : (
            <Field id="pe-id" label="Model id">
              <Input
                id="pe-id"
                value={modelId}
                onChange={(e) => setModelId(e.target.value)}
                placeholder="text-embedding-3-small"
                autoFocus
              />
            </Field>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field id="pe-dims" label="Dimensions" hint="Native vector width, if known.">
              <Input
                id="pe-dims"
                inputMode="numeric"
                value={dimensions}
                onChange={(e) => setDimensions(e.target.value)}
                placeholder="1536"
              />
            </Field>
            <Field id="pe-price" label="Price (USD / 1M tokens)">
              <Input
                id="pe-price"
                inputMode="decimal"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="0.02"
              />
            </Field>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!valid || save.isPending}>
              {save.isPending ? "Saving…" : row ? "Save" : "Add model"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Discard -----------------------------------------------------------------

export function DiscardModelDialog({
  target,
  onClose,
}: {
  target: { kind: PlatformModelKind; modelId: string } | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const kind = target?.kind ?? "llm";
  const [reason, setReason] = React.useState("");

  React.useEffect(() => {
    if (target) setReason("");
  }, [target]);

  const discard = useMutation({
    mutationFn: () => discardPlatformModel(kind, target?.modelId ?? "", reason),
    onSuccess: () => {
      toast.success(`${target?.modelId} discarded.`);
      void qc.invalidateQueries({ queryKey: qk.platformModels(kind) });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dependants = kind === "llm" ? "agents" : "knowledge bases";

  return (
    <Dialog open={target !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Discard {target?.modelId}?</DialogTitle>
          <DialogDescription>
            Tenants will stop seeing it in their pickers and cannot select it. You can restore it
            later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              Any tenant {dependants} already using this model stop working immediately: they fail
              with a &quot;model has been discarded&quot; error until the tenant points them at
              another model. Tenants see this on their own screens.
            </div>
          </div>

          <Field
            id="discard-reason"
            label="Reason (optional)"
            hint="Shown to tenants in the error, so say what to use instead."
          >
            <Textarea
              id="discard-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Superseded by gpt-5.6-terra."
            />
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={discard.isPending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => discard.mutate()}
            disabled={discard.isPending}
          >
            {discard.isPending ? "Discarding…" : "Discard model"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
